import { useCallback, useEffect, useRef, useState } from 'react';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useMiniappSdkEventHandler } from 'app/shared/hooks/use-miniapp-sdk';
import { FEED_VIEW_TTL_MS, RESTORE_TIMEOUT_MS } from 'app/shared/hooks/use-feed-view-memory';

/** Namespaced like the feed view — host storage is shared across every miniapp. */
export const SELECTION_STORAGE_KEY = 'activity-center.selection';

/**
 * The same window the feed view gets, for the same reason: this is one trip, not a preference.
 * Shared so the two can never disagree — restoring a filter without the record it was framing, or
 * the reverse, is worse than restoring neither.
 */
export const SELECTION_TTL_MS = FEED_VIEW_TTL_MS;

/**
 * What the host needs to put the pane back, plus what this app needs to light the row up again.
 *
 * The command is stored verbatim as it was SENT rather than rebuilt on read: the two would drift,
 * and the host vets the url it is given, so a rebuilt one is a second thing to keep honest.
 */
export interface StoredSelection {
  itemId: string;
  /**
   * Absent when the entry is a NOMINATION rather than a record already opened.
   *
   * The portal's home page writes one of these on the way to the activity page: it knows which
   * record the reader tapped, but not the url that opens it, because building those is this app's
   * job. An entry without a command therefore means "open this one when you can", and activating
   * it rewrites the entry with the command actually sent.
   */
  command?: unknown;
  /** The building the HOST was on. A selection made on one building's page is not the other's. */
  hostBuildingUuid: string;
  savedAt: number;
}

export const parseStoredSelection = (raw: unknown): StoredSelection | null => {
  if (typeof raw !== 'string' || !raw) return null;

  try {
    const parsed = JSON.parse(raw) as Partial<StoredSelection>;
    const { itemId, command, hostBuildingUuid, savedAt } = parsed;

    // Validated rather than trusted: the user's host owns this storage, and an older build could
    // have left a shape this one no longer understands.
    if (typeof itemId !== 'string' || !itemId) return null;
    if (typeof hostBuildingUuid !== 'string' || !hostBuildingUuid) return null;
    // `Number.isFinite` alone does not narrow the optional away, and `Infinity` is a number.
    if (typeof savedAt !== 'number' || !Number.isFinite(savedAt)) return null;

    return { itemId, command, hostBuildingUuid, savedAt };
  } catch {
    return null;
  }
};

/**
 * Bounded at BOTH ends. Only checking the upper bound let a timestamp in the future read as fresh
 * for as long as it stayed ahead of the clock — which a device whose clock moved backwards after a
 * write produces on its own, without anyone tampering.
 */
export const isSelectionFresh = (stored: StoredSelection, now = Date.now()): boolean => {
  const age = now - stored.savedAt;

  return age >= 0 && age <= SELECTION_TTL_MS;
};

/**
 * Remember which record the reader is looking at, for a host pane to restore.
 *
 * `isEnabled` is the caller's decision rather than a surface check, because the answer is not the
 * same for every tap on a given surface. What matters is whether the reader STAYS on a record:
 *
 * - The split surface writes on every record it opens — the pane is the record, and it persists.
 * - The popover writes only for the in-app booking screen, the one place there where a tap leads
 *   to a record and stays on it. Its other taps LEAVE for another miniapp, and remembering one of
 *   those would put a pane back on a later visit that the reader never asked for.
 */
export const useRememberSelection = (
  hostBuildingUuid: string | undefined,
  isEnabled: boolean,
): ((itemId: string, command: unknown) => void) => {
  const client = useMiniappSdk();

  return useCallback(
    (itemId: string, command: unknown) => {
      if (!isEnabled || !hostBuildingUuid) return;

      client?.storage?.set(SELECTION_STORAGE_KEY, {
        itemId,
        command,
        hostBuildingUuid,
        savedAt: Date.now(),
      } satisfies StoredSelection);
    },
    [client, hostBuildingUuid, isEnabled],
  );
};

/**
 * Forget the record the pane was showing.
 *
 * The stored entry answers "what does this surface currently have open", NOT "what was tapped in
 * the last few minutes" — and the difference is the whole reason this exists. Left to the TTL, a
 * reader who opened a booking, went back to the list, and then expanded to full screen arrived
 * with that booking already in the pane, minutes later, having asked for the list.
 *
 * Written rather than removed because the SDK's storage has only `get` and `set`. An empty string
 * is the tombstone: both readers of this key — `parseStoredSelection` here and the portal's own
 * `readRememberedSelection` — already treat a falsy raw value as nothing stored, so neither needs
 * to learn about it.
 */
export const useForgetSelection = (isEnabled: boolean): (() => void) => {
  const client = useMiniappSdk();

  return useCallback(() => {
    if (!isEnabled) return;

    client?.storage?.set(SELECTION_STORAGE_KEY, '');
  }, [client, isEnabled]);
};

/**
 * Give the row its highlight back on a cold start.
 *
 * The host's url already carries the record, so the PANE survives a reload on its own; the feed
 * does not, because the selection lives in component state that a reload throws away. Without this
 * a refresh leaves a pane open beside a list with nothing marked as the thing it is showing.
 *
 * Returns only an id. Where the pane goes is the host's business, and the reader may well have
 * moved on by the time the answer lands.
 */
export interface RestoredSelection {
  itemId: string;
  /** No command stored yet: nominated by the host, not yet opened into its pane. */
  needsOpening: boolean;
}

export const useRestoreSelection = (
  hostBuildingUuid: string | undefined,
  isSplitSurface: boolean,
): RestoredSelection | null => {
  const client = useMiniappSdk();
  const [restored, setRestored] = useState<RestoredSelection | null>(null);
  const isSettled = useRef(false);
  const hasRequested = useRef(false);
  /*
   * Whether this hook is still on screen — NOT whether the read is done.
   *
   * The two were one flag and could not be: settling in the cleanup made a reply arriving after
   * unmount harmless, but StrictMode's setup/cleanup/setup on mount tripped the same cleanup, so in
   * development the hook settled itself before the host ever answered and nothing was ever
   * restored. Re-armed by every setup, cleared by every teardown, so the synthetic one recovers and
   * the real one does not.
   */
  const isMounted = useRef(false);
  /*
   * When the read stops being worth applying, as an absolute time rather than a duration.
   *
   * A duration restarts every time the effect re-runs, which would let a StrictMode replay — or any
   * re-run — quietly extend a bound whose whole job is to be fixed. Held here, the deadline is set
   * once with the request and every later arming gets only what is LEFT of it.
   */
  const deadlineAt = useRef<number | null>(null);

  useEffect(() => {
    isMounted.current = true;

    const markUnmounted = () => {
      isMounted.current = false;
    };

    if (isSettled.current) return markUnmounted;

    /*
     * The surface went away with a read still out. Settled rather than merely ignored, so the reply
     * cannot restore a selection into a surface that has no pane to show it.
     *
     * Only when something was actually asked for: mounting without a split surface is the ordinary
     * case, and settling there would stop a later switch INTO split from ever asking.
     */
    if (!isSplitSurface || !client?.storage) {
      if (hasRequested.current) isSettled.current = true;

      return markUnmounted;
    }

    if (!hasRequested.current) {
      hasRequested.current = true;
      // A host with no handler registered drops the read in silence, so the answer is bounded in
      // time rather than waited on.
      deadlineAt.current = Date.now() + RESTORE_TIMEOUT_MS;
      client.storage.get(SELECTION_STORAGE_KEY);
    }

    const timer = setTimeout(
      () => {
        isSettled.current = true;
      },
      Math.max(0, (deadlineAt.current ?? Date.now()) - Date.now()),
    );

    return () => {
      clearTimeout(timer);
      markUnmounted();
    };
  }, [client, isSplitSurface]);

  useMiniappSdkEventHandler(
    'getStorageResponse',
    (data: unknown) => {
      // Keyed by what was asked for, so a reply for somebody else's key is not ours to read.
      /*
       * `isMounted` as well as `isSettled`: the sdk exposes no `off`, so this handler stays
       * registered for the life of the client and would otherwise restore into a feed that has gone.
       */
      if (!isMounted.current || isSettled.current || !data || typeof data !== 'object'
        || !(SELECTION_STORAGE_KEY in data)) {
        return;
      }

      isSettled.current = true;

      const stored = parseStoredSelection((data as Record<string, unknown>)[SELECTION_STORAGE_KEY]);
      if (!stored || !isSelectionFresh(stored) || stored.hostBuildingUuid !== hostBuildingUuid) return;

      /*
       * Absence, not falsiness. The contract is an entry with NO command (see `StoredSelection`),
       * and `parseStoredSelection` deliberately does not validate the command's shape — so `null`,
       * `''` and `0` all reach here. Reading those as "nominated" would auto-open the record on
       * every mount for as long as the malformed entry stayed fresh. Treating them as a plain
       * restore instead degrades to a highlight, which is the safe half of the behaviour.
       */
      setRestored({ itemId: stored.itemId, needsOpening: stored.command === undefined });
    },
    [hostBuildingUuid],
  );

  return restored;
};
